// @givmo/org-console — the sidebar's rows, rendered ONCE for every host (shell contract v3, T6).
//
// `sidebarFor` decides the rows; this renders them. Three shapes, and the shape IS the honesty:
//
//   link      — a real `<a href>`; the ordinary left-click hands over to the host's router when it
//               supplies `onNavigate` (the same narrow interception every link in this estate uses),
//               a cross-origin row takes the document hop.
//   request   — a greyed row with the lock glyph whose click opens the host's request dialog, asking
//               for a role in the module the row's string belongs to. A real `<button>`.
//   statement — a greyed row with the lock glyph and NO control: its reason ("Compliance is off",
//               "Books is setting up"; "You do not have access" only on a request row a host
//               offers no door for) is its status line and the second half of its accessible name.
//               A row the caller cannot use in a module they hold never reaches here: `sidebarFor`
//               leaves it out (Org Machinery OM-29).
//
// ⚠ NEVER `aria-disabled`, and never an inert-looking control that navigates. A greyed row is not
// a disabled link — it is a statement, or it is a request door — and the markup says which.
//
// The class recipe is the console shell's own row recipe, so a package row and a host row can sit in
// one list without a seam. This package holds no glyph set: a row's `icon` is whatever the host
// declared, rendered through the design system's `Icon`.
//
// DATA CONTRACT: `menu-<id>` on a live row, `menu-link-<id>` on a greyed one (both from `sidebarFor`),
// `menu-<id>-badge` on a badge — the shell's Team count or a module-supplied one (§Badges below) —
// `menu-<id>-reason` on a status line. `menu-section-<id>` on a group — present whether or not its
// caption is drawn (§Captions below). A live row whose module withholds its page (§Served below) has
// no element at all, nor a list item.

import type { MouseEvent, ReactNode } from 'react';
import {
  Badge,
  FOCUS_RING,
  Icon,
  cn,
  shouldInterceptClick,
  testIdProp,
} from '@givmo/design-system';
import type { LucideLikeIcon } from '@givmo/design-system';
import { Lock } from 'lucide-react';
import type {
  RowCountBadge,
  RowServed,
  Sidebar,
  SidebarCaptions,
  SidebarRow,
  SidebarSection,
} from './sidebar';

export type { SidebarCaptions };

const ROW_BASE =
  'group relative flex min-h-36 w-full items-center gap-10 rounded-md pl-14 pr-12 font-sans text-s14 tracking-givmo outline-none transition-colors text-left';

/**
 * The ONE list recipe: a column of rows at the row-to-row spacing. Every group's `<ul>` wears it —
 * and, with the captions hidden, so does the nav container itself (§Captions), so the distance from
 * the last row of one group to the first row of the next is this same gap and can never drift from
 * it: one literal, two sites.
 */
const LIST = 'flex flex-col gap-2';

/**
 * OM-23's 16px group gap between a group that draws its OWN caption on the hidden arm (OM-69) and a
 * group above it: the column's 2px row gap (`LIST`) plus these 14, the same 16px the shown arm puts
 * between two groups (`mb-16` on the group above). `not-first`, because a gap belongs BETWEEN two
 * groups: drawn first (its module's own list resolved empty), the group sits where any first group
 * does, as on the shown arm, where no group carries space above it. The group wrappers are the
 * column's children in order and an empty section renders nothing, so the first child is the first
 * DRAWN group — except where a section's every row is withheld by its `served` hook: that section
 * still draws its (empty) group, which is why a module puts `served` only on a row whose section
 * still draws a row whenever it would draw that one (OM-99's limit, `RowServed`; Org Machinery
 * OM-109 has the shell drop such a group).
 */
const ADMIN_GAP = 'not-first:mt-14';

/**
 * §Captions — whether a section's caption is DRAWN above its rows (Org Machinery OM-21, scoped by
 * OM-27).
 *
 * THE FRAME DECIDES (OM-27): `sidebarFor` answers `Sidebar.captions` — `'shown'` in the frame of a
 * module that declares more than one section, `'hidden'` everywhere else — and the nav draws that
 * answer; a host's `captions` prop overrides it (the capability OM-21 kept), and a sidebar that
 * carries no answer draws the original rendering. It was a HOST declaration before, both consoles
 * hidden (Jonathan, 2026-09-12, from his look at a live console — "we don't really have enough pages
 * here yet to justify having header categories for the different page links. When there are only a
 * few, the header categories just create confusion… I don't want to like delete that capability, but
 * I'd like to be able to take it away for now."), until he scoped it on 2026-09-28 to the one
 * five-page module he had been looking at — "but i dind't mean that should be applied broadly across
 * every module" (Org Machinery §OM-14, the 2026-09-28 amendment, quotes him in full). Never an env
 * flag, never a runtime knob.
 *
 * `'shown'` — the original rendering, byte-identical: the caption above each group
 * and the 16px gap between groups (`mb-16 last:mb-0` on the group wrapper).
 * `'hidden'` — no caption element at all, AND NO GROUP GAP (Org Machinery OM-23; Jonathan,
 * 2026-09-18, from his prod look at a live console: "while the session hid the header, it did not
 * get rid of the space between them, so now it looks sloppy … that random looking gap between Team
 * and Profile"). The list is one evenly spaced column — the row gap between EVERY pair of
 * consecutive rows, inside a group and across the seam between two — because the group wrapper
 * carries no spacing on this arm and the nav container is itself a `LIST` column. The caption and
 * its gap come back TOGETHER the day a host turns captions on. The group `<div>`
 * (`menu-section-<id>`), its `<ul>`, and the group order are untouched on both arms. The section's
 * name still reaches assistive tech as the list's accessible name, because the confusion Jonathan
 * named is a SIGHTED-SCAN one: a screen-reader user hears one group at a time and is helped, not
 * confused, by hearing which. `aria-label` on the `<ul>` rather than an `sr-only` caption, so the
 * DOM tells the truth — a hidden caption leaves no text node for a text query, a `getByText` or a
 * Playwright text assertion to find, and no element that can leak back into the layout.
 *
 * A SECTION'S OWN CAPTION (Org Machinery OM-69): a module may name one caption for its frame's
 * Organization section (`ModuleNav.adminCaption`, Board's "Admin"), and `sidebarFor` hands it over as
 * that section's `caption`. It is drawn on EITHER arm — whatever the frame's answer and whatever a
 * host's `captions` override says, so OM-21's host declaration can never hide it — and it brings
 * OM-23's 16px group gap with it: on the hidden arm the group sits that far below a list above it
 * (the column's row gap plus `ADMIN_GAP`; drawn first, it sits where any first group does), and
 * every other group there keeps the evenly spaced,
 * uncaptioned, named list. The caption is drawn where the section carries one and nowhere else; a
 * frame whose module declares none renders exactly as above, byte for byte.
 */
export interface OrgSidebarNavProps {
  sidebar: Sidebar<LucideLikeIcon>;
  /** Follow a same-origin row with the host's router instead of a document load. */
  onNavigate?: (href: string) => void;
  /**
   * Open the request dialog for a module — the SAME callback the tiles use. Absent ⇒ every request
   * row renders as a statement (the honesty law: no request door without a request).
   */
  onRequestAccess?: (module: string, requires: string) => void;
  /** Fired after any row is activated — a drawer closes itself on it. */
  onRowActivate?: () => void;
  /**
   * An OVERRIDE of the frame's caption answer (§Captions). Absent — the ordinary case since OM-27 —
   * the nav draws `sidebar.captions`, and `'shown'` when the sidebar carries none. It never hides a
   * section's OWN caption: a module's `adminCaption` draws in its frame whatever this says (OM-69).
   */
  captions?: SidebarCaptions;
  /**
   * §Trailing — the ONE row the shell appends AFTER every declared row (Org Machinery OM-22): the
   * Settings row, from `settingsRowFor`, which stays OUTSIDE `sidebarFor` so the rule keeps
   * computing declarations only and no module can add or remove it.
   *
   * Jonathan, 2026-09-12, reversing D-CPO-35 (1) and the 2026-09-05 mockup pass: "we had also made
   * a ruling that the Settings page link will always be in the bottom left, but I did not actually
   * intend for that to be added to the little box with the user avatar where you sign out. Instead
   * of being there, I just want Settings to always be the last page in the left panel." On spacing:
   * "I don't want the settings gap to be different than any other page spacing, just need it to
   * always be the last row."
   *
   * So it renders as the final `<li>` of the LAST drawn group's own `<ul>` — the same list, so the
   * same `gap-2` as any two rows, no group of its own, no caption, no extra gap — and it scrolls
   * with the list. When no group is drawn at all (every section empty, the floor), it gets a list of
   * its own so the row still exists; that is the only case where it is not inside a group.
   *
   * ⚠ The consequence, accepted rather than overlooked: inside the last group, the row is within
   * that group's accessible name. It is the SAME grouping a sighted reader will see the day captions
   * return (the §OM-17 amendment notes the row will then sit under the last caption, and calls that
   * a design call for that day) — so what AT hears and what the eye sees agree, which is the point.
   */
  trailingRow?: SidebarRow<LucideLikeIcon>;
  /**
   * The navigate for the trailing row ALONE — the host's SETTINGS navigate, never the generic in-app
   * hop, because the row must carry the same origin the gear would from this page (D-CPO-33). A
   * trailing row with no navigate is still a real `<a href>`; the browser takes it.
   */
  onTrailingNavigate?: (href: string) => void;
  /** Classes for the nav's own scroll container — the host owns its padding and overflow. */
  className?: string;
}

function RowGlyph({
  icon,
  active,
  muted,
}: {
  icon: LucideLikeIcon | undefined;
  active: boolean;
  muted: boolean;
}) {
  if (icon === undefined) return null;
  return (
    <Icon
      icon={icon}
      size={18}
      color="currentColor"
      className={muted ? 'text-icon-muted' : active ? 'text-brand-600' : 'text-icon'}
      aria-hidden
    />
  );
}

/**
 * §Badges — the ONE badge a row wears, drawn by the shell whoever supplied the number: the shell's own
 * Team count (`row.badge`, off the standing read) or a count the MODULE supplies for one of its rows
 * (`row.countBadge`, Org Machinery OM-30). The shell owns the look and the honesty — nothing while
 * the count is zero, unknown or not a finite number — and the module never draws its own.
 */
function RowBadge({ count, testId }: { count: number | null | undefined; testId: string }) {
  if (count === undefined || count === null || !Number.isFinite(count) || count <= 0) return null;
  return (
    <Badge tone="brand" size="sm" aria-label={`${count} waiting`} testId={`${testId}-badge`}>
      {count}
    </Badge>
  );
}

/**
 * A module-supplied count (OM-30). Its own component so the module's hook runs ONLY while this row
 * renders live — a stable hook per row, so the rules of hooks hold across renders.
 */
function CountedRowBadge({ source, testId }: { source: RowCountBadge; testId: string }) {
  const count = source.useCount();
  return <RowBadge count={count} testId={testId} />;
}

function LiveRow({
  row,
  onNavigate,
  onRowActivate,
}: {
  row: SidebarRow<LucideLikeIcon>;
  onNavigate: OrgSidebarNavProps['onNavigate'];
  onRowActivate: OrgSidebarNavProps['onRowActivate'];
}) {
  const href = row.href ?? '#';
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onRowActivate?.();
    if (row.external || onNavigate === undefined || !shouldInterceptClick(event)) return;
    event.preventDefault();
    onNavigate(href);
  };
  return (
    <a
      href={href}
      onClick={handleClick}
      aria-current={row.active ? 'page' : undefined}
      className={cn(
        ROW_BASE,
        FOCUS_RING,
        row.active
          ? 'bg-surface-active font-w600 text-brand-700'
          : 'font-w500 text-text-secondary hover:bg-surface-hover hover:text-text-strong',
      )}
      {...testIdProp(row.testId)}
    >
      {row.active && (
        <span
          aria-hidden
          className="absolute left-[5px] top-1/2 h-16 w-[3px] -translate-y-1/2 rounded-full bg-brand-600"
        />
      )}
      <RowGlyph icon={row.icon} active={row.active} muted={false} />
      <span className="min-w-0 flex-1 truncate">{row.label}</span>
      {row.countBadge === undefined ? (
        <RowBadge count={row.badge} testId={row.testId} />
      ) : (
        <CountedRowBadge source={row.countBadge} testId={row.testId} />
      )}
    </a>
  );
}

function GreyedRow({
  row,
  onRequestAccess,
  onRowActivate,
}: {
  row: SidebarRow<LucideLikeIcon>;
  onRequestAccess: OrgSidebarNavProps['onRequestAccess'];
  onRowActivate: OrgSidebarNavProps['onRowActivate'];
}) {
  const reason = row.reason ?? '';
  const lock = <Icon icon={Lock} size={14} className="shrink-0 text-icon-muted" aria-hidden />;
  const label = (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate">{row.label}</span>
      {reason.length > 0 && (
        <span
          className="truncate font-sans text-s11 font-w400 tracking-givmo text-text-tertiary"
          {...testIdProp(`${row.testId}-reason`)}
        >
          {reason}
        </span>
      )}
    </span>
  );
  const requestable =
    row.state === 'request' && row.requestModule !== undefined && onRequestAccess !== undefined;
  if (requestable) {
    const module = row.requestModule as string;
    return (
      <button
        type="button"
        onClick={() => {
          onRowActivate?.();
          onRequestAccess(module, row.requires);
        }}
        className={cn(
          ROW_BASE,
          FOCUS_RING,
          'py-4 font-w500 text-text-tertiary hover:bg-surface-hover',
        )}
        aria-label={`${row.label} — ${reason}. Request access.`}
        {...testIdProp(row.testId)}
      >
        <RowGlyph icon={row.icon} active={false} muted />
        {label}
        {lock}
      </button>
    );
  }
  return (
    <div
      // A `note`, not a bare `div`: `aria-label` is prohibited on a generic element and AT would
      // read nothing but the text; a note may be named, so the reason reaches AT as its name too.
      role="note"
      className={cn(ROW_BASE, 'cursor-default py-4 font-w500 text-text-tertiary')}
      aria-label={`${row.label} — ${reason}`}
      {...testIdProp(row.testId)}
    >
      <RowGlyph icon={row.icon} active={false} muted />
      {label}
      {lock}
    </div>
  );
}

/**
 * §Served — a live row whose page the MODULE says is served (Org Machinery OM-99): its own component,
 * so the module's hook runs only while the row would render live, a stable hook per row (the rules of
 * hooks hold across renders), as the count's does. Only a settled `true` draws the list item; until
 * then, and on `false`, the row leaves no element and no list item, so the gap between the rows
 * either side is the one row gap (OM-23) and the trailing row stays last (OM-22).
 */
function ServedRowItem({ source, children }: { source: RowServed; children: ReactNode }) {
  const served = source.useServed();
  return served === true ? <li>{children}</li> : null;
}

/** One row as a list item: a live row whose module answers whether its page is served goes through
 *  §Served; every other row is the list item it always was. */
function SidebarRowItem({
  row,
  onNavigate,
  onRequestAccess,
  onRowActivate,
}: {
  row: SidebarRow<LucideLikeIcon>;
  onNavigate: OrgSidebarNavProps['onNavigate'];
  onRequestAccess: OrgSidebarNavProps['onRequestAccess'];
  onRowActivate: OrgSidebarNavProps['onRowActivate'];
}) {
  const view = (
    <OrgSidebarRowView
      row={row}
      onNavigate={onNavigate}
      onRequestAccess={onRequestAccess}
      onRowActivate={onRowActivate}
    />
  );
  if (row.served !== undefined && row.state === 'link' && row.href !== null) {
    return <ServedRowItem source={row.served}>{view}</ServedRowItem>;
  }
  return <li>{view}</li>;
}

export function OrgSidebarRowView({
  row,
  onNavigate,
  onRequestAccess,
  onRowActivate,
}: {
  row: SidebarRow<LucideLikeIcon>;
  onNavigate?: OrgSidebarNavProps['onNavigate'];
  onRequestAccess?: OrgSidebarNavProps['onRequestAccess'];
  onRowActivate?: OrgSidebarNavProps['onRowActivate'];
}) {
  if (row.state === 'link' && row.href !== null) {
    return <LiveRow row={row} onNavigate={onNavigate} onRowActivate={onRowActivate} />;
  }
  return <GreyedRow row={row} onRequestAccess={onRequestAccess} onRowActivate={onRowActivate} />;
}

function SectionView({
  section,
  captions,
  children,
  trailing,
}: {
  section: SidebarSection<LucideLikeIcon>;
  captions: SidebarCaptions;
  children: ReactNode;
  /** The shell's trailing row (§Trailing), on the LAST drawn group only — inside its `<ul>`. */
  trailing?: ReactNode;
}) {
  if (section.rows.length === 0) return null;
  const hidden = captions === 'hidden';
  // The section's OWN caption (OM-69) is drawn on either arm; otherwise its label, on the shown arm.
  const caption = section.caption ?? (hidden ? undefined : section.label);
  return (
    // The INTER-GROUP gap belongs to the group and goes WITH the caption (OM-23): `mb-16 last:mb-0`
    // with the captions drawn — the original rendering — and no spacing at all with them hidden,
    // where the nav container is the column that spaces the groups at the row gap (§Captions) — but
    // for a group drawing its own caption there, which keeps the 16px between it and a group above
    // (`ADMIN_GAP`; none when it is drawn first).
    <div
      className={
        hidden ? (section.caption === undefined ? undefined : ADMIN_GAP) : 'mb-16 last:mb-0'
      }
      {...testIdProp(`menu-section-${section.id}`)}
    >
      {caption === undefined ? null : (
        <p className="mb-4 px-12 font-sans text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
          {caption}
        </p>
      )}
      {/* Named only when no caption is drawn: with one drawn, naming the list would make AT read the
          section's word twice. `undefined` emits no attribute, so the shown arm is unchanged. */}
      <ul className={LIST} aria-label={caption === undefined ? section.label : undefined}>
        {children}
        {trailing}
      </ul>
    </div>
  );
}

/** The whole sidebar body: every section, every row, under the rule — then the trailing row. */
export function OrgSidebarNav({
  sidebar,
  onNavigate,
  onRequestAccess,
  onRowActivate,
  captions = sidebar.captions ?? 'shown',
  trailingRow,
  onTrailingNavigate,
  className,
}: OrgSidebarNavProps) {
  // An empty section renders nothing (`SectionView` returns null), so the LAST section in the data
  // is not necessarily the last one DRAWN — and the trailing row must land in the one that is drawn,
  // or it would vanish with an empty group. Compared by identity, never by id: ids are unique by
  // construction, but a list whose tail can disappear is the wrong place to rely on that.
  const drawn = sidebar.sections.filter((section) => section.rows.length > 0);
  const lastDrawn = drawn.length > 0 ? drawn[drawn.length - 1] : undefined;
  const trailing =
    trailingRow === undefined ? null : (
      <li key={trailingRow.id}>
        <OrgSidebarRowView
          row={trailingRow}
          onNavigate={onTrailingNavigate}
          onRowActivate={onRowActivate}
        />
      </li>
    );
  const hidden = captions === 'hidden';
  return (
    // With the captions hidden the container is the ONE evenly spaced column (§Captions, OM-23):
    // its gap is the row gap and a group wrapper contributes no spacing, so the last row of one
    // group and the first of the next sit exactly as any two rows do. With them drawn the host's
    // classes pass through untouched — the original rendering, byte for byte.
    <div className={hidden ? cn(className, LIST) : className}>
      {sidebar.sections.map((section) => (
        <SectionView
          key={section.id}
          section={section}
          captions={captions}
          trailing={section === lastDrawn ? trailing : undefined}
        >
          {section.rows.map((row) => (
            <SidebarRowItem
              key={row.id}
              row={row}
              onNavigate={onNavigate}
              onRequestAccess={onRequestAccess}
              onRowActivate={onRowActivate}
            />
          ))}
        </SectionView>
      ))}
      {/* No group was drawn (the floor: every section empty, or none declared). The row still
          exists — the same list recipe, so the same row, just with nothing above it. */}
      {lastDrawn === undefined && trailing !== null && <ul className={LIST}>{trailing}</ul>}
    </div>
  );
}
